// Copyright (c) 2025 WiseTime. All rights reserved.

import { EMPTY, Observable, concat } from "rxjs"
import { Action } from "redux"
import { filter, mergeMap, withLatestFrom } from "rxjs/operators"
import * as Actions from "../actions"
import * as Slice from "../slices/timeline"
import { RootState } from "../../../app/store"
import * as UserContextSlice from "../../user-context/slice"
import { isDisabledTimeGroup, groupUnpostedTimesheetFieldOptionsBySiteId } from "../timeGroups"
import { generateTimesheetFieldCachesRefreshActions } from "./lib/generateTimesheetFieldCachesRefreshActions"

export const refreshTimesheetFieldConfigs = () =>
  (action$: Observable<Action>, state$: Observable<RootState>): Observable<Action> =>
    action$.pipe(
      filter((action) => 
        UserContextSlice.setCurrentSiteId.match(action) ||
        Actions.refreshTimesheetFieldConfigs.match(action)
      ),
      withLatestFrom(state$),
      mergeMap(([_, state]) => {
        const unpostedTimeGroups = Object.values(Slice.selectTimeGroupsCache(state))
          .filter(timeGroup => !isDisabledTimeGroup(timeGroup.postStatus))
        if (!unpostedTimeGroups.length) {
          return EMPTY
        }
        
        const actions: Action[] = []
        
        // Generate timesheet field configs for unposted time groups
        actions.push(Actions.generateTimesheetFieldConfigs(unpostedTimeGroups))
        
        // Batch get timesheet field options from unposted time groups 
        const unpostedOptionsBySiteId = groupUnpostedTimesheetFieldOptionsBySiteId(unpostedTimeGroups)
        const cachedTimesheetFields = Object.values(state.timeline.timesheetFieldsCache)
        const cachedTimesheetFieldOptions = state.timeline.timesheetFieldOptionsCache
        const requiredTimesheetFieldActions = generateTimesheetFieldCachesRefreshActions(
          unpostedOptionsBySiteId,
          cachedTimesheetFields,
          cachedTimesheetFieldOptions,
        )
        actions.push(...requiredTimesheetFieldActions)
        
        return concat(actions)
      }),
    )

export default refreshTimesheetFieldConfigs()
